import React from "react";
import DocViewer, { DocViewerRenderers } from "@cyntler/react-doc-viewer";
import { XMarkIcon, ArrowDownTrayIcon } from "@heroicons/react/24/outline";

/**
 * Document Preview Component
 * Displays documents using react-doc-viewer in a modal
 *
 * Supported formats:
 * - PDF (.pdf)
 * - Word (.doc, .docx)
 * - Excel (.xls, .xlsx)
 * - Images (.jpg, .jpeg, .png, .gif)
 * - Text (.txt)
 */
const DocumentPreview = ({ isOpen, onClose, document, downloadUrl }) => {
  if (!isOpen) return null;

  // Prepare document for viewer
  const docs = document
    ? [
        {
          uri: document.uri || document.url || downloadUrl,
          fileName: document.name || document.fileName || "Document",
          fileType: document.type || document.fileType || getFileTypeFromName(document.name),
        },
      ]
    : [];

  // Extract file type from filename
  function getFileTypeFromName(filename) {
    if (!filename) return "";
    const ext = filename.split(".").pop()?.toLowerCase();
    return ext || "";
  }

  // Handle download
  const handleDownload = () => {
    if (downloadUrl || document?.uri || document?.url) {
      const url = downloadUrl || document?.uri || document?.url;
      const link = window.document.createElement("a");
      link.href = url;
      link.download = document?.name || document?.fileName || "document";
      link.click();
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-blue-100">
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-gray-900 truncate">
                {document?.name || document?.fileName || "Document Preview"}
              </h3>
              {document?.description && (
                <p className="text-sm text-gray-600 truncate">
                  {document.description}
                </p>
              )}
            </div>
            <div className="flex items-center space-x-2 ml-4">
              {/* Download Button */}
              {(downloadUrl || document?.uri || document?.url) && (
                <button
                  onClick={handleDownload}
                  className="p-2 text-blue-700 bg-blue-100 hover:bg-blue-200 rounded-lg transition-all duration-200"
                  title="Download document"
                >
                  <ArrowDownTrayIcon className="h-5 w-5" />
                </button>
              )}
              {/* Close Button */}
              <button
                onClick={onClose}
                className="p-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-all duration-200"
                title="Close preview"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Document Viewer */}
          <div className="flex-1 overflow-auto p-4" style={{ maxHeight: 'calc(90vh - 80px)' }}>
            {docs.length > 0 ? (
              <div className="h-full min-h-[600px]" style={{ overflow: 'auto' }}>
                <DocViewer
                  documents={docs}
                  pluginRenderers={DocViewerRenderers}
                  config={{
                    header: {
                      disableHeader: true,
                      disableFileName: true,
                    },
                    pdfZoom: {
                      defaultZoom: 1.0,
                      zoomJump: 0.2,
                    },
                    pdfVerticalScrollByDefault: true,
                    loadingRenderer: {
                      overrideComponent: () => (
                        <div className="flex items-center justify-center h-full">
                          <div className="text-center">
                            <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
                            <p className="text-gray-600">Loading document...</p>
                          </div>
                        </div>
                      ),
                    },
                  }}
                  style={{
                    height: "100%",
                    width: "100%",
                    borderRadius: "0.5rem",
                    overflow: "auto",
                  }}
                  theme={{
                    primary: "#3b82f6",
                    secondary: "#e5e7eb",
                    tertiary: "#f3f4f6",
                    textPrimary: "#1f2937",
                    textSecondary: "#6b7280",
                    textTertiary: "#9ca3af",
                    disableThemeScrollbar: false,
                  }}
                />
              </div>
            ) : (
              <div className="flex items-center justify-center h-full bg-gray-50 rounded-lg">
                <div className="text-center">
                  <p className="text-gray-600 font-medium">No document available for preview</p>
                  <p className="text-sm text-gray-500 mt-2">Please try again later</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentPreview;
